import React, { useState, useEffect, useRef } from 'react';
import { mediaItems } from '../media';

const PortfolioOverview = ({ onEnter3D }) => {
  const [titleRevealed, setTitleRevealed] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [cursorInCenter, setCursorInCenter] = useState(false);
  const lineRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const leftLineCenter = screenWidth * 0.25; // 25% from left
      const rightLineCenter = screenWidth * 0.75; // 75% from left
      
      const distanceToLeftLine = Math.abs(e.clientX - leftLineCenter);
      const distanceToRightLine = Math.abs(e.clientX - rightLineCenter);
      
      // Trigger when mouse gets within 100px of either vertical line
      const nearLine = distanceToLeftLine < 100 || distanceToRightLine < 100;
      
      // Also check if cursor is in center area for enhanced effects
      const centerX = screenWidth / 2;
      const centerY = screenHeight / 2;
      const distanceToCenter = Math.sqrt(
        Math.pow(e.clientX - centerX, 2) + Math.pow(e.clientY - centerY, 2)
      );
      const isInCenter = distanceToCenter < 150;
      
      setCursorInCenter(isInCenter);
      
      // Reveal symbol when cursor approaches vertical lines
      if (nearLine && !titleRevealed) {
        setTitleRevealed(true);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, [titleRevealed]);

  const handleTitleClick = () => {
    if (titleRevealed && !isTransitioning) {
      setIsTransitioning(true);
      // Faster transition to 3D gallery
      onEnter3D();
    }
  };

  return (
    <div className="w-full h-screen flex relative overflow-hidden">
      {/* Left Half - Black Background */}
      <div className="w-1/2 h-full bg-black relative"></div>
      
      {/* Right Half - White Background */}
      <div className="w-1/2 h-full bg-white relative"></div>
      
      {/* Main Content */}
      <div className="absolute inset-0 z-10 flex items-center justify-center">
        {/* White Fluorescent Line on Black Side - Rotates when hovering diamond */}
        <div 
          className={`absolute left-1/4 top-0 w-1 h-full transform -translate-x-1/2 z-20 transition-all duration-500 origin-center ${
            cursorInCenter ? 'rotate-90' : 'rotate-0'
          }`}
          style={{
            background: 'linear-gradient(180deg, transparent 0%, #ffffff 20%, #ffffff 80%, transparent 100%)',
            boxShadow: '0 0 40px rgba(255, 255, 255, 0.8), 0 0 80px rgba(255, 255, 255, 0.4)',
            filter: 'brightness(1.5)'
          }}
        ></div>
        
        {/* Black Fluorescent Line on White Side - Rotates when hovering diamond */}
        <div 
          ref={lineRef}
          className={`absolute left-3/4 top-0 w-1 h-full transform -translate-x-1/2 z-20 transition-all duration-500 origin-center ${
            cursorInCenter ? 'rotate-90' : 'rotate-0'
          }`}
          style={{
            background: 'linear-gradient(180deg, transparent 0%, #000000 20%, #000000 80%, transparent 100%)',
            boxShadow: '0 0 40px rgba(0, 0, 0, 0.8), 0 0 80px rgba(0, 0, 0, 0.4)',
            filter: 'brightness(0.5)'
          }}
        ></div>

        {/* Center Lighting Effect */}
        <div 
          className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-all duration-500 ${
            cursorInCenter ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            width: '400px',
            height: '400px',
            background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.08) 30%, transparent 70%)',
            borderRadius: '50%',
            pointerEvents: 'none'
          }}
        />
        
        {/* Entry Symbol - appears with lighting effect */}
        <div 
          className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-all duration-400 ease-out ${
            titleRevealed 
              ? 'opacity-100 cursor-pointer hover:scale-110 hover:rotate-90' 
              : 'opacity-0'
          }`}
          onClick={handleTitleClick}
        >
          {/* Minimal geometric symbol - diamond/rhombus */}
          <div 
            className="w-8 h-8 transition-all duration-300"
            style={{
              background: cursorInCenter 
                ? 'linear-gradient(45deg, #ffffff 0%, #ffffff 49%, #000000 51%, #000000 100%)'
                : 'linear-gradient(45deg, #ffffff 0%, #ffffff 49%, #000000 51%, #000000 100%)',
              transform: 'rotate(45deg)',
              boxShadow: cursorInCenter 
                ? '0 0 20px rgba(255, 255, 255, 0.3), 0 0 40px rgba(255, 255, 255, 0.1)' 
                : '0 0 10px rgba(255, 255, 255, 0.2)',
              filter: cursorInCenter ? 'brightness(1.3)' : 'brightness(1)'
            }}
          />
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
      `}</style>
    </div>
  );
};

export default PortfolioOverview;
